import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PackagesContext } from "../context/PackagesContext";
import { ThemeContext } from "../context/ThemeContext";
import { StarIcon, HeartIcon } from "@heroicons/react/24/solid";
import { FiArrowRight } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { addEnquiriesData } from "../services/enquirieService";

const AnimatedPackageList = () => {
  const { packages: contextPackages } = useContext(PackagesContext);
  const { theme } = useContext(ThemeContext);
  const [searchParams] = useSearchParams();
  const destinationParam = searchParams.get("destination");
  const countryParam = searchParams.get("country");
  const navigate = useNavigate();

  const [favorites, setFavorites] = useState(new Set());
  const [filteredPackages, setFilteredPackages] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    travelDate: "",
    destination: "",
    leavingFrom: "",
    totalPerson: "",
    bookingWithin: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const filtered = contextPackages.filter((pkg) => {
      const matchesDestination = destinationParam
        ? pkg.destination?.trim().toLowerCase() === destinationParam.trim().toLowerCase()
        : true;

      const matchesCountry = countryParam
        ? pkg.country?.trim().toLowerCase() === countryParam.trim().toLowerCase()
        : true;

      return matchesDestination && matchesCountry;
    });

    setFilteredPackages(filtered);
  }, [contextPackages, destinationParam, countryParam]);

  const packages = useMemo(() => {
    return filteredPackages.map((pkg) => ({
      id: pkg.id,
      name: pkg.packageName,
      price: pkg.price,
      country: pkg.country,
      destination: pkg.destination,
      rating: 4.5,
      image: pkg.image2 || pkg.image1 || pkg.image3 || pkg.image4 || "/placeholder.jpg",
    }));
  }, [filteredPackages]);

  const toggleFavorite = (id) => {
    const updated = new Set(favorites);
    updated.has(id) ? updated.delete(id) : updated.add(id);
    setFavorites(updated);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.travelDate) newErrors.travelDate = "Travel date is required";
    if (!formData.destination) newErrors.destination = "Destination is required";
    if (!formData.leavingFrom) newErrors.leavingFrom = "Departure city is required";
    if (!formData.totalPerson) newErrors.totalPerson = "Number of adults is required";
    if (!formData.bookingWithin) newErrors.bookingWithin = "Booking timeline is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        ...formData,
        totalDays: 7,
        totalPerson: parseInt(formData.totalPerson),
        bookingWithin:
          formData.bookingWithin === "1-2 days" ? 2 :
          formData.bookingWithin === "1 Week" ? 7 :
          formData.bookingWithin === "2 Weeks" ? 14 : 30,
      };
      await addEnquiriesData(payload);
      navigate("/thank-you");
      setFormData({
        name: "", phone: "", travelDate: "", destination: "", leavingFrom: "",
        totalPerson: "", bookingWithin: "",
      });
    } catch (err) {
      toast.error("Failed to submit enquiry");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 pt-32 pb-24 px-4 sm:px-6 md:px-10 lg:px-16">
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-800 dark:text-white mb-10 mt-5 sm:mb-12">
        {countryParam === "UAE"
          ? "Dubai"
          : countryParam === "Indonesia"
            ? "Bali"
            : countryParam || "Our"} Tour Packages
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Enquiry Form */}
        <motion.div
          className="w-full h-fit sm:w-fit max-w-full bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-xl mx-auto lg:mx-0"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center text-gray-800 dark:text-white">
            Get Your Custom Quote
          </h2>

          <form className="space-y-4 text-sm sm:text-base" onSubmit={handleSubmit}>
            {[{ id: "name", label: "Name", type: "text" },
              { id: "phone", label: "Phone", type: "tel" },
              { id: "travelDate", label: "Travel Date", type: "date" }].map(({ id, label, type }) => (
              <div key={id}>
                <label htmlFor={id} className="block font-medium text-gray-800 dark:text-white">{label}</label>
                <input
                  id={id}
                  type={type}
                  value={formData[id]}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 mt-1 rounded-lg border ${errors[id] ? "border-red-500" : "border-gray-300"} bg-white dark:bg-gray-700 text-gray-800 dark:text-white`}
                />
                {errors[id] && <p className="text-xs text-red-500">{errors[id]}</p>}
              </div>
            ))}

            {[{
              id: "destination", label: "Destination", options: [
                "Dubai", "Thailand", "Maldives", "Bali", "Singapore",
                "Sri Lanka", "Malaysia", "Vietnam", "Kerala", "Goa",
                "Himachal", "Kashmir", "Other"
              ],
            }, {
              id: "leavingFrom", label: "Departure City", options: [
                "Ahmedabad", "Delhi", "Mumbai", "Bangalore", "Hyderabad",
                "Pune", "Chennai", "Kolkata", "Other"
              ],
            }, {
              id: "totalPerson", label: "Adults", options: [1, 2, 3, 4, 5],
            }, {
              id: "bookingWithin", label: "Booking Within", options: ["1-2 days", "1 Week", "2 Weeks", "1 Month"],
            }].map(({ id, label, options }) => (
              <div key={id}>
                <label htmlFor={id} className="block font-medium text-gray-800 dark:text-white">{label}</label>
                <select
                  id={id}
                  value={formData[id]}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 mt-1 rounded-lg border ${errors[id] ? "border-red-500" : "border-gray-300"} bg-white dark:bg-gray-700 text-gray-800 dark:text-white`}
                >
                  <option value="">Select {label}</option>
                  {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                {errors[id] && <p className="text-xs text-red-500">{errors[id]}</p>}
              </div>
            ))}

            <button
              type="submit"
              className="w-full mt-4 flex items-center justify-center gap-2 bg-blue-600 text-white text-sm sm:text-base px-4 py-3 rounded-lg hover:bg-blue-700 transition"
            >
              Get Free Quote <FiArrowRight />
            </button>
          </form>
        </motion.div>

        {/* Packages Section with Scroll */}
        <div className="lg:col-span-2">
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 max-h-[1000px] overflow-y-auto pr-2">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-lg h-fit"
              >
                <div className="relative">
                  <img src={pkg.image} alt={pkg.name} className="w-full h-48 sm:h-56 object-cover" />
                  <button
                    onClick={() => toggleFavorite(pkg.id)}
                    className="absolute top-4 right-4 bg-white dark:bg-gray-700 p-2 rounded-full"
                  >
                    <HeartIcon className={`h-6 w-6 ${favorites.has(pkg.id) ? "text-red-500" : "text-gray-300"}`} />
                  </button>
                </div>
                <div className="p-4 text-sm sm:text-base">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{pkg.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-300">{pkg.destination}, {pkg.country}</p>
                  <div className="flex items-center gap-1 text-yellow-500 my-2">
                    <StarIcon className="w-5 h-5" /> <span>{pkg.rating}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-blue-600 font-bold">₹{pkg.price.toLocaleString()}</p>
                    <button
                      onClick={() => navigate(`/packages-description?destination=${pkg.id}`)}
                      className="text-white bg-blue-500 px-3 py-2 rounded-lg hover:bg-blue-600 text-xs sm:text-sm"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedPackageList;
